/**
 * Shared normalization for product/company hour-cutoff fields (OrderProduct.hourLock,
 * OrderProduct.day_hour_locks, CofcConf.day_time_locks). Keeps the persisted format
 * consistent (zero-padded "HH:mm") across company defaults and per-product overrides so the
 * Kotlin/Python/TS lock evaluators never disagree on parsing.
 */

const VALID_DAY_KEY = /^[1-7]$/;

/** Pads "9:30" -> "09:30" and validates the result is a real 00:00-23:59 time. Returns '' when invalid/blank.
 * Also accepts Date/time-picker values (reads getHours/getMinutes) since several forms store hourLock as a Date. */
export const normalizeHourLock = (raw: unknown): string => {
    if (raw && typeof raw === 'object' && typeof (raw as Date).getHours === 'function') {
        const d = raw as Date;
        return normalizeHourLock(`${d.getHours()}:${d.getMinutes()}`);
    }
    const value = String(raw ?? '').trim();
    if (!value) return '';
    const match = value.match(/^(\d{1,2}):(\d{1,2})$/);
    if (!match) return '';
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (!Number.isInteger(hour) || !Number.isInteger(minute)) return '';
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return '';
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

/** Parses a day->hour JSON map (or already-parsed object), normalizing values and dropping
 * invalid day keys ("1".."7") or blank/unparsable times. Never throws. */
export const parseWeekdayHourMap = (raw: unknown): Record<string, string> => {
    let parsed: unknown = raw;
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed || trimmed === 'None' || trimmed === 'null') return {};
        try {
            parsed = JSON.parse(trimmed);
        } catch {
            return {};
        }
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, string> = {};
    Object.entries(parsed as Record<string, unknown>).forEach(([day, value]) => {
        if (!VALID_DAY_KEY.test(day)) return;
        const normalized = normalizeHourLock(value);
        if (normalized) out[day] = normalized;
    });
    return out;
};

/** Serializes a day->hour map back to the persisted JSON string, pruning invalid/blank entries. */
export const serializeWeekdayHourMap = (map: Record<string, string> | null | undefined): string =>
    JSON.stringify(parseWeekdayHourMap(map ?? {}));

/** A recurring "order until weekday+time" cutoff rule for one DELIVERY weekday (see
 * OrderProduct.delivery_cutoff_rules), e.g. delivery weekday 1 (Sunday) closes for ordering on
 * weekday 5 (Thursday) at 14:00. */
export interface DeliveryCutoffRule {
    weekday: string; // ordering weekday "1".."7" the cutoff falls on
    time: string; // "HH:mm"
}

/**
 * Repair mimshak quote-stripped maps like `{1:{weekday:5,time:23:00}}` back into JSON.
 * Priority product sync used to strip `"` from delivery_cutoff_rules / day_hour_locks.
 */
export const repairMangledJsonObject = (raw: string): string | null => {
    const trimmed = raw.trim();
    if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null;
    if (trimmed.includes('"')) return null; // already looks like JSON (or mixed); don't guess
    try {
        const repaired = trimmed
            .replace(/([,{]\s*)(\d+)(\s*:)/g, '$1"$2"$3')
            .replace(/([,{]\s*)([A-Za-z_][A-Za-z0-9_]*)(\s*:)/g, '$1"$2"$3')
            .replace(/:(\d{1,2}:\d{2})([,}])/g, ':"$1"$2');
        JSON.parse(repaired);
        return repaired;
    } catch {
        return null;
    }
};

/** Parses order_products.delivery_cutoff_rules (JSON keyed by DELIVERY weekday "1".."7"):
 * {"1":{"weekday":5,"time":"14:00"}}. Drops invalid delivery/cutoff weekdays or blank times.
 * Also accepts mimshak-stripped `{1:{weekday:5,time:23:00}}` and recovers it. */
export const parseDeliveryCutoffRules = (raw: unknown): Record<string, DeliveryCutoffRule> => {
    let parsed: unknown = raw;
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed || trimmed === 'None' || trimmed === 'null') return {};
        try {
            parsed = JSON.parse(trimmed);
        } catch {
            const repaired = repairMangledJsonObject(trimmed);
            if (!repaired) return {};
            try {
                parsed = JSON.parse(repaired);
            } catch {
                return {};
            }
        }
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, DeliveryCutoffRule> = {};
    Object.entries(parsed as Record<string, any>).forEach(([deliveryDay, entry]) => {
        if (!VALID_DAY_KEY.test(deliveryDay) || !entry || typeof entry !== 'object') return;
        const weekday = String(Number((entry as any).weekday));
        const time = normalizeHourLock((entry as any).time);
        if (!VALID_DAY_KEY.test(weekday) || !time) return;
        out[deliveryDay] = { weekday, time };
    });
    return out;
};

/** Serializes a delivery-weekday->cutoff-rule map back to the persisted JSON string. */
export const serializeDeliveryCutoffRules = (map: Record<string, DeliveryCutoffRule> | null | undefined): string =>
    JSON.stringify(
        Object.fromEntries(
            Object.entries(parseDeliveryCutoffRules(map ?? {})).map(([day, rule]) => [
                day,
                { weekday: Number(rule.weekday), time: rule.time },
            ])
        )
    );

export const DAY_ORDER = ['1', '2', '3', '4', '5', '6', '7'] as const;
export const DAY_SHORT_LABELS: Record<string, string> = {
    '1': 'א',
    '2': 'ב',
    '3': 'ג',
    '4': 'ד',
    '5': 'ה',
    '6': 'ו',
    '7': 'ש',
};

/** Human-readable "9:30 (א,ה) / כללי 14:00" style summary for admin grids and tooltips. */
export const describeHourLockPrecedence = (
    hourLock: string | null | undefined,
    dayHourLocks: Record<string, string> | string | null | undefined,
    companyDefault?: string | null,
): string => {
    const flat = normalizeHourLock(hourLock);
    const perDay = parseWeekdayHourMap(dayHourLocks);
    const parts: string[] = [];
    const perDayEntries = DAY_ORDER.filter((d) => perDay[d]).map((d) => `${DAY_SHORT_LABELS[d]} ${perDay[d]}`);
    if (perDayEntries.length) parts.push(perDayEntries.join(', '));
    if (flat) parts.push(`כללי ${flat}`);
    const company = normalizeHourLock(companyDefault);
    if (!flat && !perDayEntries.length && company) parts.push(`ברירת מחדל חברה ${company}`);
    return parts.length ? parts.join(' · ') : 'ללא נעילת שעה';
};
